import React, { useState, useEffect } from "react";
import { arrayOf, func, string, array, bool } from "prop-types";
import { useTranslation } from "@lib/i18n";
import { useRouter } from "next/router";

import DeliveryPointMap from "./DeliveryPointMap";
import AddressTextField from "../../../shared/AddressTextField";
import Typography from "../../../shared/Typography";
import SimpleButton from "../../../shared/SimpleButton";

import gtm from "@gtm/core";
import { deliveryPointType, addressType, latLngType } from "@types";
import { calculateDistanceInMetersAsync } from "@lib/google-maps/calculateDistance";
import { revertProgressEvent } from "@gtm/events/shared";
import { basketDealerListUpdateUrlEvent } from "@gtm/events/basket";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { STEPS } from "@gtm/constants";
import { COUNTRIES } from "@shared/constants";
import { getIsCountry } from "lib/getIsCountry";

const createMapMarkers = async (deliveryPoints, deliveryAddress, latLng) => {
  const unsortedDeliveryPoints = await Promise.all(
    deliveryPoints.map(async point => {
      const lat = parseFloat(point.geoLatitude);
      const lng = parseFloat(point.geoLongitude);

      const distance = await calculateDistanceInMetersAsync(
        { lat, lng },
        latLng
      );

      const distanceMetric = () => {
        return getIsCountry(COUNTRIES.GB) ? 0.621371 : 1;
      };

      return {
        ...point,
        geoLongitude: lng,
        geoLatitude: lat,
        // markerIcon: getStaticAssetURL(
        //   "/images/delivery-map/delivery-point.png"
        // ),
        distance:
          deliveryAddress && latLng
            ? (Math.round((distance * distanceMetric()) / 10) / 100).toFixed(2)
            : undefined,
      };
    })
  );

  const targetId = deliveryAddress?._id;

  return [...unsortedDeliveryPoints].sort((a, b) => {
    if (a._id === targetId) {
      return -1;
    }

    if (b._id === targetId) {
      return 1;
    }

    return a.distance - b.distance;
  });
};

const MapSelection = ({
  affiliation,
  deliveryAddress,
  setDeliveryAddress,
  onNextState,
  onPrevState,
  inputMapAddress,
  latLng,
  pageVariant,
  setInputMapAddress,
  setLatLng,
  deliveryPoints,
  isSSRRendered,
}) => {
  const { t } = useTranslation();
  const [activeDeliveryPointId, setActiveDeliveryPointId] = useState(
    deliveryAddress?._id
  );
  const router = useRouter();
  const isB2BSelected = useIsB2BSelected();

  const [markers, setMarkers] = useState([]);

  useEffect(() => {
    createMapMarkers(deliveryPoints, deliveryAddress, latLng).then(
      targetMarkers => {
        setMarkers(targetMarkers);
      }
    );
  }, [deliveryPoints, deliveryAddress, latLng]);

  const handlePointChange = pointId => {
    setActiveDeliveryPointId(pointId);
    setDeliveryAddress(markers.find(({ _id }) => _id === pointId));
  };

  const handlePointSelect = pointId => {
    handlePointChange(pointId);
    onNextState();
  };

  const handleSetAddress = address => {
    const { geoLat: lat, geoLng: lng } = address;
    setDeliveryAddress(address);
    setLatLng({ lat, lng });
    setInputMapAddress(address);
  };

  const handleReset = () => {
    setInputMapAddress(null);
    setDeliveryAddress(null);
    setLatLng(null);
  };

  useEffect(() => {
    gtm.fire(
      basketDealerListUpdateUrlEvent({
        pageTitle: router.asPath,
        pageVariant,
        affiliation,
        isB2BSelected,
        isSSRRendered,
      })
    );
  }, []);

  return (
    <>
      <SimpleButton
        className="px-0 py-2 m-0"
        dataId="delivery-back-button"
        onClick={() => {
          onPrevState();
          gtm.fire(revertProgressEvent(STEPS.DELIVERY));
        }}
        withArrow
      >
        {t("basket.back")}
      </SimpleButton>
      <AddressTextField
        onSetAddress={handleSetAddress}
        onResetAddress={handleReset}
        initialValue={inputMapAddress}
      />
      <Typography className="text-sm my-4">
        {t("basket.delivery.chooseDeliveryPoint")}
      </Typography>
      <DeliveryPointMap
        activeDeliveryPointId={activeDeliveryPointId}
        focusDeliveryAddress={handlePointChange}
        latLng={latLng}
        onDeliveryPointSelect={handlePointSelect}
        points={markers}
      />
    </>
  );
};

MapSelection.propTypes = {
  affiliation: string,
  setDeliveryAddress: func.isRequired,
  points: arrayOf(deliveryPointType),
  deliveryAddress: addressType,
  onNextState: func,
  onPrevState: func,
  setInputMapAddress: func.isRequired,
  setLatLng: func.isRequired,
  latLng: latLngType,
  inputMapAddress: addressType,
  pageVariant: string.isRequired,
  deliveryPoints: array.isRequired,
  isSSRRendered: bool,
};

export default MapSelection;
